import { isDefined } from "@clipboard-health/util-ts";
import { IconButton, OutlinedInput, type OutlinedInputProps } from "@mui/material";
import { forwardRef, type Ref, useState } from "react";

import { mergeSxProps } from "../../lib/mergeSxProps";
import { CbhIcon, type IconType } from "../CbhIcon";
import { Text } from "../Text";

export interface InputProps extends Omit<
  OutlinedInputProps,
  "startAdornment" | "endAdornment" | "multiline"
> {
  iconType?: IconType;
  unit?: string;
  /**
   * When true, adds a password visibility toggle button with eye/eye-slash icons.
   * Defaults to false.
   */
  secret?: boolean;
  /**
   * Called when the Enter key is pressed. Also works for the Return key on iOS/Android keyboards.
   */
  onEnterPress?: (input: HTMLInputElement) => void;
}

function BaseInput(props: InputProps, ref: Ref<HTMLInputElement>) {
  const { iconType, unit, secret = false, sx, onEnterPress, size, type, ...rest } = props;
  const [isSecretVisible, setIsSecretVisible] = useState(false);

  const handleToggleSecretVisibility = () => {
    setIsSecretVisible((previous) => !previous);
  };

  const inputType = secret && !isSecretVisible ? "password" : type;

  const hasUnit = isDefined(unit) && unit !== "";

  const secretToggleButton = secret ? (
    <IconButton
      edge="end"
      size={size}
      aria-label={isSecretVisible ? "Hide" : "Show"}
      onClick={handleToggleSecretVisibility}
    >
      <CbhIcon type={isSecretVisible ? "eye-slash" : "eye"} />
    </IconButton>
  ) : null;

  const endAdornmentContent = (
    <>
      {hasUnit && (
        <Text
          variant={size === "large" ? "body1" : "body2"}
          sx={mergeSxProps([{ color: (theme) => theme.palette.text?.secondary }], sx)}
        >
          {unit}
        </Text>
      )}
      {secretToggleButton}
    </>
  );

  return (
    <OutlinedInput
      {...rest}
      ref={ref}
      size={size}
      type={inputType}
      startAdornment={iconType ? <CbhIcon type={iconType} size="small" /> : undefined}
      endAdornment={hasUnit || secret ? endAdornmentContent : undefined}
      sx={mergeSxProps(
        (theme) => ({
          boxShadow: props.value && !props.disabled ? theme.shadows[2] : undefined,
        }),
        sx
      )}
      onKeyDown={
        onEnterPress
          ? (event) => {
              if (event.key === "Enter" && event.target instanceof HTMLInputElement) {
                onEnterPress(event.target);
              }

              rest.onKeyDown?.(event);
            }
          : rest.onKeyDown
      }
    />
  );
}

export const Input = forwardRef(BaseInput);
